import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, Mail, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setEmailSent(true);
      toast.success('Email enviado! Verifica a sua caixa de entrada.');
    } catch (error) {
      // Still show success to prevent email enumeration
      setEmailSent(true);
      toast.success('Se o email estiver registado, receberá instruções.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex">
        {/* Left - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1920)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Right - Success Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Verifica o teu email
            </h2>
            
            <p className="text-muted-foreground mb-8">
              Se o email <strong className="text-foreground">{email}</strong> estiver registado na nossa plataforma, 
              receberá um link para redefinir a tua palavra-passe.
            </p>
            
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/login')}
                data-testid="back-to-login"
                className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
              >
                Voltar ao Login
              </Button>
              
              <Button
                variant="ghost"
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                className="w-full text-muted-foreground"
              >
                Enviar para outro email
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mt-8">
              Não recebeu o email? Verifica a pasta de spam ou aguarda alguns minutos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-brand text-4xl font-bold text-foreground mb-4">
            Recuperar acesso
          </h2>
          <p className="text-muted-foreground text-lg max-w-md">
            Não se preocupe, acontece a todos. Vamos ajudá-lo a recuperar o acesso à tua conta.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/login')}
            className="mb-8 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar ao Login
          </Button>

          {/* Logo */}
          <Link to="/" className="block mb-8">
            <img src="/logo-red.png" alt="Intimate.pt" className="h-10 w-auto" />
          </Link>

          <div className="w-14 h-14 bg-ruby/20 rounded-full flex items-center justify-center mb-6">
            <Mail className="w-7 h-7 text-ruby" />
          </div>

          <h2 className="text-2xl font-semibold text-foreground mb-2">
            Esqueceste-te da palavra-passe?
          </h2>
          <p className="text-muted-foreground mb-8">
            Introduza o teu email e enviaremos instruções para redefinir a tua palavra-passe.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email"
                className="bg-surface border-border h-12"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="forgot-password-submit"
              className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
            >
              {loading ? 'A enviar...' : 'Enviar instruções'}
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-8">
            Lembrou-se da palavra-passe?{' '}
            <Link to="/login" className="text-ruby hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
